'use client'

import type React from 'react'
import { useMemo } from 'react'

import { keepPreviousData } from '@tanstack/react-query'
import { styled } from 'styled-components'

import {
  AnalyticsEntityType,
  AnalyticsSource,
  NestableAnalyticsProvider,
} from '@b-stock/fe-core/bstock-react'
import type { AllListingsListing } from '@b-stock/search-api-client'

import Carousel from '@/components/common/carousel/Carousel'
import ListingCard from '@/components/listings/listingCard/ListingCard'
import { defaultContextValue } from '@/components/listings/search/ListingSearchProvider'
import ListingSearchContext from '@/components/listings/search/ListingSearchProvider/ListingSearchContext'
import { ScreenName } from '@/helpers/telemetry/ScreenName'
import listingCardsQuery, { isPriced } from '@/queries/listingCardsQuery'

// Matches the card's own min-width; display: grid stretches each card to the
// row's tallest so their bottoms line up.
const CardWrapper = styled.div`
  display: grid;
  width: 14.25rem;
  height: 100%;
`

// ListingCard reads search state from context. Overriding
// getActiveFilterCount marks this as a provided value rather than the default.
const contextValue = {
  ...defaultContextValue,
  getActiveFilterCount: () => 0,
  isLoading: false,
}

type Props = {
  /** The carousel's listings, unfiltered, so the key matches the server prefetch. */
  listings: AllListingsListing[] | undefined
  carousel_name?: string
  screenName?: string
  /** Shown until the prices arrive, and if no card can be shown. */
  fallback: React.ReactNode
}

export default function ListingCardCarousel({
  listings,
  carousel_name,
  screenName,
  fallback,
}: Props) {
  const { data } = listingCardsQuery.useWithOpts([listings ?? []], {
    enabled: !!listings?.length,
    placeholderData: keepPreviousData,
    // A failed lookup keeps the auction cards rather than calling the price
    // services again on retry, refocus or reconnect.
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  // SMC types its screen name as a plain string; its callers pass ScreenName values.
  const screen = (screenName ?? ScreenName.Homepage) as ScreenName

  const analyticsData = useMemo(
    () => ({
      carousel_name,
      screenName: screen,
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
    }),
    [carousel_name, screen]
  )

  // Positions count every listing, as the impression does, so a skipped card
  // leaves a gap instead of shifting the cards after it.
  const cards = (data ?? [])
    .map((listing, index) => ({ listing, position: index + 1 }))
    .filter(
      ({ listing }) =>
        !!listing.auctionUrl && !!listing.storefrontId && isPriced(listing)
    )

  if (!cards.length) return fallback

  return (
    <NestableAnalyticsProvider value={analyticsData}>
      <ListingSearchContext.Provider value={contextValue}>
        <Carousel>
          {cards.map(({ listing, position }) => (
            <CardWrapper key={listing.id}>
              <ListingCard
                listing={listing}
                screenName={screen}
                position={position}
              />
            </CardWrapper>
          ))}
        </Carousel>
      </ListingSearchContext.Provider>
    </NestableAnalyticsProvider>
  )
}
